// ApplicantDatabase.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../../config';
import './databases.css';

function ApplicantCard() {
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [applicants, setApplicants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [applicantSearch, setApplicantSearch] = useState('');
  const [applicantSortKey, setApplicantSortKey] = useState('');
  const [applicantSortOrder, setApplicantSortOrder] = useState('asc');
  const [showApplicantModal, setShowApplicantModal] = useState(false);
  const [editApplicant, setEditApplicant] = useState(null);
  const [newApplicant, setNewApplicant] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    source: 'CRM Manual Entry',
    status: 'new',
    property_interest: '',
    budget_min: '',
    budget_max: '',
    move_in_date: '',
    lease_length: '',
    notes: '',
    lead_score: 85,
    assigned_agent: '',
    last_contact: new Date().toISOString().split('T')[0]
  });
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Column management state
  const [hiddenColumns, setHiddenColumns] = useState({});
  const [columnWidths, setColumnWidths] = useState({});
  const [showColumnSettings, setShowColumnSettings] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [resizeColumn, setResizeColumn] = useState(null);
  const [resizeStartX, setResizeStartX] = useState(0);
  const [resizeStartWidth, setResizeStartWidth] = useState(0);
  const [showExcelUploadModal, setShowExcelUploadModal] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(null);

  const MAX_VISIBLE_COLUMNS = 10;

  useEffect(() => {
    const storedUser = localStorage.getItem('username');
    if (!storedUser) {
      navigate('/');
    } else {
      setUsername(storedUser);
    }
    loadApplicants();
  }, [navigate]);

  // Add mouse event listeners for column resizing
  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isResizing, resizeColumn, resizeStartX, resizeStartWidth]);

  // No dropdown for actions; button will navigate directly to dashboard

  const loadApplicants = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await fetch(`${API_BASE_URL}/api/applicants`);
      if (response.ok) {
        const data = await response.json();
        setApplicants(data);
      } else {
        setError('Failed to load applicants');
        setApplicants([]);
      }
    } catch (error) {
      console.error('Error loading applicants:', error);
      setError('Error loading applicants');
      setApplicants([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddApplicant = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/applicants`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newApplicant),
      });

      if (response.ok) {
        const result = await response.json();
        console.log('Applicant created:', result);
        
        // Refresh data
        loadApplicants();
        setShowApplicantModal(false);
        setNewApplicant({
          first_name: '',
          last_name: '',
          email: '',
          phone: '',
          source: 'CRM Manual Entry',
          status: 'new',
          property_interest: '',
          budget_min: '',
          budget_max: '',
          move_in_date: '',
          lease_length: '',
          notes: '',
          lead_score: 85,
          assigned_agent: '',
          last_contact: new Date().toISOString().split('T')[0]
        });
      } else {
        const errorData = await response.json();
        setError(`Error creating applicant: ${errorData.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error creating applicant:', error);
      setError('Error creating applicant. Please try again.');
    }
  };

  const handleUpdateApplicant = async (applicantId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/applicants/${applicantId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(editApplicant),
      });

      if (response.ok) {
        const result = await response.json();
        console.log('Applicant updated:', result);
        
        // Refresh data
        loadApplicants();
        setEditApplicant(null);
      } else {
        const errorData = await response.json();
        setError(`Error updating applicant: ${errorData.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error updating applicant:', error);
      setError('Error updating applicant. Please try again.');
    }
  };

  const handleDeleteApplicant = async (applicantId) => {
    if (!window.confirm('Are you sure you want to delete this applicant?')) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/applicants/${applicantId}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        console.log('Applicant deleted successfully');
        loadApplicants();
      } else {
        const errorData = await response.json();
        setError(`Error deleting applicant: ${errorData.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error deleting applicant:', error);
      setError('Error deleting applicant. Please try again.');
    }
  };

  const handleSort = (key) => {
    if (applicantSortKey === key) {
      setApplicantSortOrder(applicantSortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setApplicantSortKey(key);
      setApplicantSortOrder('asc');
    }
  };

  const getSourceIcon = (source) => {
    switch (source) {
      case 'Website': return '🌐';
      case 'Referral': return '👥';
      case 'Social Media': return '📱';
      case 'Direct Contact': return '📞';
      case 'Email Campaign': return '📧';
      case 'Open House': return '🏠';
      default: return '📋';
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'new': return 'status-badge new';
      case 'qualified': return 'status-badge qualified';
      case 'contacted': return 'status-badge contacted';
      case 'viewing': return 'status-badge viewing';
      case 'application': return 'status-badge application';
      case 'approved': return 'status-badge approved';
      case 'rejected': return 'status-badge rejected';
      case 'proposal': return 'status-badge proposal';
      case 'negotiation': return 'status-badge negotiation';
      case 'closed_won': return 'status-badge closed-won';
      case 'closed_lost': return 'status-badge closed-lost';
      // Background check and screening statuses
      case 'background': return 'status-badge background';
      case 'background_check': return 'status-badge background_check';
      case 'screening': return 'status-badge screening';
      case 'review': return 'status-badge review';
      // Lease-related statuses
      case 'lease_signed': return 'status-badge lease_signed';
      case 'lease': return 'status-badge lease';
      // Move-in statuses
      case 'move_in': return 'status-badge move_in';
      case 'movein': return 'status-badge movein';
      case 'moved_in': return 'status-badge moved_in';
      default: return 'status-badge new';
    }
  };

  // Column management functions
  const toggleColumnVisibility = (columnKey) => {
    setHiddenColumns(prev => ({
      ...prev,
      [columnKey]: !prev[columnKey]
    }));
  };

  const resetColumnWidths = () => {
    setColumnWidths({});
  };

  const handleColumnResize = (columnKey, newWidth) => {
    setColumnWidths(prev => ({
      ...prev,
      [columnKey]: Math.max(100, newWidth)
    }));
  };

  const handleMouseDown = (e, columnKey) => {
    e.preventDefault();
    setIsResizing(true);
    setResizeColumn(columnKey);
    setResizeStartX(e.clientX);
    setResizeStartWidth(columnWidths[columnKey] || 150);
  };

  const handleMouseMove = (e) => {
    if (!isResizing || !resizeColumn) return;
    
    const deltaX = e.clientX - resizeStartX;
    const newWidth = resizeStartWidth + deltaX;
    handleColumnResize(resizeColumn, newWidth);
  };

  const handleMouseUp = () => {
    setIsResizing(false);
    setResizeColumn(null);
  };

  // Excel upload functions
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setUploadFile(file);
    }
  };

  const handleExcelUpload = async () => {
    if (!uploadFile) return;
    
    setUploadProgress('Uploading...');
    // TODO: Implement Excel upload functionality
    setTimeout(() => {
      setUploadProgress('Upload complete!');
      setTimeout(() => {
        setShowExcelUploadModal(false);
        setUploadFile(null);
        setUploadProgress(null);
        loadApplicants(); // Reload data
      }, 1000);
    }, 2000);
  };

  // Filter and sort applicants
  const filteredApplicants = applicants
    .filter(applicant => {
      if (!applicantSearch) return true;
      const searchLower = applicantSearch.toLowerCase();
      return (
        (applicant.first_name && applicant.first_name.toLowerCase().includes(searchLower)) ||
        (applicant.last_name && applicant.last_name.toLowerCase().includes(searchLower)) ||
        (applicant.email && applicant.email.toLowerCase().includes(searchLower)) ||
        (applicant.phone && applicant.phone.includes(searchLower)) ||
        (applicant.source && applicant.source.toLowerCase().includes(searchLower))
      );
    })
    .sort((a, b) => {
      if (!applicantSortKey) return 0;
      
      let aValue = a[applicantSortKey];
      let bValue = b[applicantSortKey];
      
      // Handle different data types
      if (applicantSortKey === 'created_at' || applicantSortKey === 'last_contact') {
        aValue = new Date(aValue || 0);
        bValue = new Date(bValue || 0);
      } else if (applicantSortKey === 'lead_score' || applicantSortKey === 'budget_min' || applicantSortKey === 'budget_max') {
        aValue = parseFloat(aValue) || 0;
        bValue = parseFloat(bValue) || 0;
      } else {
        aValue = (aValue || '').toString().toLowerCase();
        bValue = (bValue || '').toString().toLowerCase();
      }
      
      if (applicantSortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

  // Pagination
  const totalPages = Math.ceil(filteredApplicants.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const paginatedApplicants = filteredApplicants.slice(startIndex, startIndex + rowsPerPage);

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading applicants...</p>
      </div>
    );
  }

  return (
    <>
            <div className="notion-card">
        <div className="notion-header">
          <h2 className="notion-title">Applicant Database</h2>
          <div className="notion-actions">
            <input
              type="text"
              placeholder="Search Applicants"
              value={applicantSearch}
              onChange={(e) => setApplicantSearch(e.target.value)}
              className="search"
            />
            <button 
              className="notion-button"
              onClick={() => handleSort('first_name')}
            >
              Sort by Name
            </button>
            <button 
              className="notion-button notion-button-primary"
              onClick={() => setShowApplicantModal(true)}
            >
              + New Applicant
            </button>
            <button 
              className="notion-button"
              onClick={() => setShowExcelUploadModal(true)}
            >
              📊 Upload CSV
            </button>
            <button 
              className="notion-button"
              onClick={() => setShowColumnSettings(true)}
            >
              ⚙️ Column Settings
            </button>
          </div>
        </div>

      {error && <div className="error">{error}</div>}
      
      <div className="notion-table-container">
        <table className="notion-table">
          <thead>
            <tr>
              <th 
                className="sortable" 
                onClick={() => handleSort('first_name')}
                style={{ width: columnWidths.first_name || 'auto' }}
              >
                First Name {applicantSortKey === 'first_name' && (applicantSortOrder === 'asc' ? '↑' : '↓')}
              </th>
              <th 
                className="sortable" 
                onClick={() => handleSort('last_name')}
                style={{ width: columnWidths.last_name || 'auto' }}
              >
                Last Name {applicantSortKey === 'last_name' && (applicantSortOrder === 'asc' ? '↑' : '↓')}
              </th>
              <th 
                className="sortable" 
                onClick={() => handleSort('email')}
                style={{ width: columnWidths.email || 'auto' }}
              >
                Email {applicantSortKey === 'email' && (applicantSortOrder === 'asc' ? '↑' : '↓')}
              </th>
              <th 
                className="sortable" 
                onClick={() => handleSort('phone')}
                style={{ width: columnWidths.phone || 'auto' }}
              >
                Phone {applicantSortKey === 'phone' && (applicantSortOrder === 'asc' ? '↑' : '↓')}
              </th>
              <th 
                className="sortable" 
                onClick={() => handleSort('source')}
                style={{ width: columnWidths.source || 'auto' }}
              >
                Source {applicantSortKey === 'source' && (applicantSortOrder === 'asc' ? '↑' : '↓')}
              </th>
              <th 
                className="sortable" 
                onClick={() => handleSort('property_interest')}
                style={{ width: columnWidths.property_interest || 'auto' }}
              >
                Property Interest {applicantSortKey === 'property_interest' && (applicantSortOrder === 'asc' ? '↑' : '↓')}
              </th>
              <th 
                className="sortable" 
                onClick={() => handleSort('status')}
                style={{ width: columnWidths.status || 'auto' }}
              >
                Status {applicantSortKey === 'status' && (applicantSortOrder === 'asc' ? '↑' : '↓')}
              </th>
              <th 
                className="sortable" 
                onClick={() => handleSort('lead_score')}
                style={{ width: columnWidths.lead_score || 'auto' }}
              >
                Lead Score {applicantSortKey === 'lead_score' && (applicantSortOrder === 'asc' ? '↑' : '↓')}
              </th>
              <th 
                className="sortable" 
                onClick={() => handleSort('assigned_agent')}
                style={{ width: columnWidths.assigned_agent || 'auto' }}
              >
                Assigned Agent {applicantSortKey === 'assigned_agent' && (applicantSortOrder === 'asc' ? '↑' : '↓')}
              </th>
              <th 
                className="sortable" 
                onClick={() => handleSort('last_contact')}
                style={{ width: columnWidths.last_contact || 'auto' }}
              >
                Last Contact {applicantSortKey === 'last_contact' && (applicantSortOrder === 'asc' ? '↑' : '↓')}
              </th>
              <th style={{ width: columnWidths.actions || 'auto' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedApplicants.map((applicant) => (
              <tr key={applicant.id}>
                <td>
                  <div className="cell-content">
                    {applicant.first_name}
                  </div>
                </td>
                <td>
                  <div className="cell-content">
                    {applicant.last_name}
                  </div>
                </td>
                <td>
                  <div className="cell-content">
                    {applicant.email}
                  </div>
                </td>
                <td>
                  <div className="cell-content">
                    {applicant.phone || 'N/A'}
                  </div>
                </td>
                <td>
                  <div className="cell-content">
                    {applicant.source}
                  </div>
                </td>
                <td>
                  <div className="cell-content">
                    {applicant.property_interest || 'N/A'}
                  </div>
                </td>
                <td>
                  <div className="cell-content">
                    <span className={getStatusBadgeClass(applicant.status)}>
                      {applicant.status.toUpperCase()}
                    </span>
                  </div>
                </td>
                <td>
                  <div className="cell-content">
                    {applicant.lead_score || 0}
                  </div>
                </td>
                <td>
                  <div className="cell-content">
                    {applicant.assigned_agent || 'Unassigned'}
                  </div>
                </td>
                <td>
                  <div className="cell-content">
                    <span className="date-cell">
                      {applicant.last_contact ? new Date(applicant.last_contact).toLocaleDateString() : 'N/A'}
                    </span>
                  </div>
                </td>
                <td>
                  <div className="cell-content">
                    <div className="action-cell">
                      <button
                        className="notion-action"
                        title="Open Dashboard"
                        onClick={(e) => { e.stopPropagation(); navigate(`/applicant/${applicant.id}`); }}
                      >
                        ⋮
                      </button>
                    </div>
                  </div>
                </td>
              </tr>
            ))}
            {paginatedApplicants.length === 0 && !loading && (
              <tr>
                <td colSpan={100} className="notion-td" style={{ textAlign: 'center', padding: 0, border: 'none' }}>
                  <div className="db-empty-state" style={{ border: 'none', borderRadius: 0 }}>
                    <div className="db-empty-state-icon">📋</div>
                    <div className="db-empty-state-title">No applicants yet</div>
                    <div className="db-empty-state-text">Add your first applicant to start tracking rental applications.</div>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>

        <div className="db-pagination">
          <div className="db-pagination-left">
            <label>Rows per page:</label>
            <select value={rowsPerPage} onChange={(e) => { setRowsPerPage(Number(e.target.value)); setCurrentPage(1); }}>
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
            </select>
          </div>
          <div className="db-pagination-center">
            Showing {filteredApplicants.length === 0 ? 0 : startIndex + 1} to {Math.min(startIndex + rowsPerPage, filteredApplicants.length)} of {filteredApplicants.length} applicants
          </div>
          <div className="db-pagination-right">
            <button className="db-pagination-btn" onClick={() => setCurrentPage(Math.max(1, currentPage - 1))} disabled={currentPage === 1}>Previous</button>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) pageNum = i + 1;
              else if (currentPage <= 3) pageNum = i + 1;
              else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
              else pageNum = currentPage - 2 + i;
              return (
                <button key={pageNum} className={`db-pagination-btn ${currentPage === pageNum ? 'active' : ''}`} onClick={() => setCurrentPage(pageNum)}>{pageNum}</button>
              );
            })}
            <button className="db-pagination-btn" onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))} disabled={currentPage === totalPages}>Next</button>
          </div>
        </div>
      </div>
    </div>

    {/* Add Applicant Modal */}

    {/* Add Applicant Modal */}
    {showApplicantModal && (
        <div className="modal-overlay" onClick={() => setShowApplicantModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add New Applicant</h2>
              <button className="modal-close" onClick={() => setShowApplicantModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <div className="form-group">
                  <label>First Name *</label>
                  <input
                    type="text"
                    value={newApplicant.first_name}
                    onChange={(e) => setNewApplicant({...newApplicant, first_name: e.target.value})}
                    placeholder="Enter first name"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Last Name *</label>
                  <input
                    type="text"
                    value={newApplicant.last_name}
                    onChange={(e) => setNewApplicant({...newApplicant, last_name: e.target.value})}
                    placeholder="Enter last name"
                    required
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Email *</label>
                  <input
                    type="email"
                    value={newApplicant.email}
                    onChange={(e) => setNewApplicant({...newApplicant, email: e.target.value})}
                    placeholder="Enter email"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Phone</label>
                  <input
                    type="tel"
                    value={newApplicant.phone}
                    onChange={(e) => setNewApplicant({...newApplicant, phone: e.target.value})}
                    placeholder="Enter phone"
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Source</label>
                  <select
                    value={newApplicant.source}
                    onChange={(e) => setNewApplicant({...newApplicant, source: e.target.value})}
                  >
                    <option value="Website">Website</option>
                    <option value="Referral">Referral</option>
                    <option value="Social Media">Social Media</option>
                    <option value="Direct Contact">Direct Contact</option>
                    <option value="Email Campaign">Email Campaign</option>
                    <option value="Open House">Open House</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Status</label>
                  <select
                    value={newApplicant.status}
                    onChange={(e) => setNewApplicant({...newApplicant, status: e.target.value})}
                  >
                    <option value="new">New</option>
                    <option value="qualified">Qualified</option>
                    <option value="contacted">Contacted</option>
                    <option value="viewing">Viewing</option>
                    <option value="application">Application</option>
                    <option value="background">Background Check</option>
                    <option value="screening">Screening</option>
                    <option value="review">Review</option>
                    <option value="proposal">Proposal</option>
                    <option value="negotiation">Negotiation</option>
                    <option value="approved">Approved</option>
                    <option value="lease_signed">Lease Signed</option>
                    <option value="move_in">Move In</option>
                    <option value="moved_in">Moved In</option>
                    <option value="closed_won">Closed Won</option>
                    <option value="closed_lost">Closed Lost</option>
                    <option value="rejected">Rejected</option>
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label>Lead Score</label>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={newApplicant.lead_score}
                  onChange={(e) => setNewApplicant({...newApplicant, lead_score: parseInt(e.target.value)})}
                  className="score-slider"
                />
                <div className="score-display">{newApplicant.lead_score}/100</div>
              </div>
              <div className="form-group">
                <label>Notes</label>
                <textarea
                  value={newApplicant.notes}
                  onChange={(e) => setNewApplicant({...newApplicant, notes: e.target.value})}
                  placeholder="Enter notes about the applicant"
                  rows="3"
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="notion-button" onClick={() => setShowApplicantModal(false)}>Cancel</button>
              <button className="notion-button notion-button-primary" onClick={handleAddApplicant}>Add Applicant</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Applicant Modal */}
      {editApplicant && (
        <div className="modal-overlay" onClick={() => setEditApplicant(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edit Applicant</h2>
              <button className="modal-close" onClick={() => setEditApplicant(null)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <div className="form-group">
                  <label>First Name *</label>
                  <input
                    type="text"
                    value={editApplicant.first_name}
                    onChange={(e) => setEditApplicant({...editApplicant, first_name: e.target.value})}
                    placeholder="Enter first name"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Last Name *</label>
                  <input
                    type="text"
                    value={editApplicant.last_name}
                    onChange={(e) => setEditApplicant({...editApplicant, last_name: e.target.value})}
                    placeholder="Enter last name"
                    required
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Email *</label>
                  <input
                    type="email"
                    value={editApplicant.email}
                    onChange={(e) => setEditApplicant({...editApplicant, email: e.target.value})}
                    placeholder="Enter email"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Phone</label>
                  <input
                    type="tel"
                    value={editApplicant.phone}
                    onChange={(e) => setEditApplicant({...editApplicant, phone: e.target.value})}
                    placeholder="Enter phone"
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Source</label>
                  <select
                    value={editApplicant.source}
                    onChange={(e) => setEditApplicant({...editApplicant, source: e.target.value})}
                  >
                    <option value="Website">Website</option>
                    <option value="Referral">Referral</option>
                    <option value="Social Media">Social Media</option>
                    <option value="Direct Contact">Direct Contact</option>
                    <option value="Email Campaign">Email Campaign</option>
                    <option value="Open House">Open House</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Status</label>
                  <select
                    value={editApplicant.status}
                    onChange={(e) => setEditApplicant({...editApplicant, status: e.target.value})}
                  >
                    <option value="new">New</option>
                    <option value="qualified">Qualified</option>
                    <option value="contacted">Contacted</option>
                    <option value="viewing">Viewing</option>
                    <option value="application">Application</option>
                    <option value="background">Background Check</option>
                    <option value="screening">Screening</option>
                    <option value="review">Review</option>
                    <option value="proposal">Proposal</option>
                    <option value="negotiation">Negotiation</option>
                    <option value="approved">Approved</option>
                    <option value="lease_signed">Lease Signed</option>
                    <option value="move_in">Move In</option>
                    <option value="moved_in">Moved In</option>
                    <option value="closed_won">Closed Won</option>
                    <option value="closed_lost">Closed Lost</option>
                    <option value="rejected">Rejected</option>
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label>Lead Score</label>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={editApplicant.lead_score}
                  onChange={(e) => setEditApplicant({...editApplicant, lead_score: parseInt(e.target.value)})}
                  className="score-slider"
                />
                <div className="score-display">{editApplicant.lead_score}/100</div>
              </div>
              <div className="form-group">
                <label>Notes</label>
                <textarea
                  value={editApplicant.notes}
                  onChange={(e) => setEditApplicant({...editApplicant, notes: e.target.value})}
                  placeholder="Enter notes about the applicant"
                  rows="3"
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="notion-button" onClick={() => setEditApplicant(null)}>Cancel</button>
              <button className="notion-button notion-button-primary" onClick={() => handleUpdateApplicant(editApplicant.id)}>Update Applicant</button>
            </div>
          </div>
        </div>
      )}

      {/* Column Settings Modal */}
      {showColumnSettings && (
        <div className="modal-overlay" onClick={() => setShowColumnSettings(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Column Settings</h2>
              <button className="modal-close" onClick={() => setShowColumnSettings(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Visible Columns:</label>
                <div className="checkbox-group">
                  {['First Name', 'Last Name', 'Email', 'Phone', 'Source', 'Property Interest', 'Status', 'Lead Score', 'Assigned Agent', 'Actions'].map(column => (
                    <label key={column} className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={!hiddenColumns[column.toLowerCase().replace(' ', '_')]}
                        onChange={() => toggleColumnVisibility(column.toLowerCase().replace(' ', '_'))}
                      />
                      {column}
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="notion-button notion-button-primary" onClick={() => setShowColumnSettings(false)}>
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CSV Upload Modal */}
      {showExcelUploadModal && (
        <div className="modal-overlay" onClick={() => setShowExcelUploadModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Upload CSV File</h2>
              <button className="modal-close" onClick={() => setShowExcelUploadModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Select CSV File:</label>
                <input
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  onChange={handleFileUpload}
                  className="file-input"
                />
                {uploadFile && (
                  <p className="file-info">Selected: {uploadFile.name}</p>
                )}
              </div>
              {uploadProgress && (
                <div className="upload-progress">
                  <p>{uploadProgress}</p>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="notion-button" onClick={() => setShowExcelUploadModal(false)}>
                Cancel
              </button>
              <button 
                className="notion-button notion-button-primary" 
                onClick={handleExcelUpload}
                disabled={!uploadFile || uploadProgress}
              >
                Upload
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default ApplicantCard;
